import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    // Initialize from localStorage immediately for faster load
    try {
      const savedCart = localStorage.getItem('cart');
      return savedCart ? JSON.parse(savedCart) : [];
    } catch {
      return [];
    }
  });
  const [loading, setLoading] = useState(false);
  const { isAuthenticated, token, loading: authLoading } = useAuth();
  const hasInitialized = useRef(false);

  // Load cart from localStorage for guests or when auth finishes loading
  useEffect(() => {
    if (authLoading) return;
    
    if (!isAuthenticated && !hasInitialized.current) {
      hasInitialized.current = true;
      const savedCart = localStorage.getItem('cart');
      if (savedCart) {
        try {
          setItems(JSON.parse(savedCart));
        } catch (e) {
          
        }
      }
    }
  }, [isAuthenticated, authLoading]);

  // Save cart to localStorage for guests
  useEffect(() => {
    if (!isAuthenticated && !authLoading) {
      localStorage.setItem('cart', JSON.stringify(items));
    }
  }, [items, isAuthenticated, authLoading]);

  // Fetch cart from server for authenticated users
  const fetchCart = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/cart`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const cartItems = response.data.items.map(item => ({
        ...item.product,
        quantity: item.quantity
      }));
      setItems(cartItems);
    } catch (error) {
      
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, token]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchCart();
    }
  }, [isAuthenticated, fetchCart]);

  const addToCart = async (product) => {
    // Check if already in cart
    const existingIndex = items.findIndex(item => item.id === product.id);
    
    if (existingIndex >= 0) {
      // Already in cart, don't add again for digital products
      return;
    }

    const newItem = { ...product, quantity: 1 };
    setItems(prev => [...prev, newItem]);

    if (isAuthenticated) {
      try {
        await axios.post(`${API}/cart/add`, {
          product_id: product.id,
          quantity: 1
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        
      }
    }
  };

  const removeFromCart = async (productId) => {
    setItems(prev => prev.filter(item => item.id !== productId));

    if (isAuthenticated) {
      try {
        await axios.delete(`${API}/cart/${productId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        
      }
    }
  };

  const clearCart = async () => {
    setItems([]);
    localStorage.removeItem('cart');

    if (isAuthenticated) {
      try {
        await axios.delete(`${API}/cart`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } catch (error) {
        
      }
    }
  };

  const getTotal = () => {
    return items.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getItemCount = () => {
    return items.reduce((count, item) => count + item.quantity, 0);
  };

  const isInCart = (productId) => {
    return items.some(item => item.id === productId);
  };

  return (
    <CartContext.Provider value={{
      items,
      loading,
      addToCart,
      removeFromCart,
      clearCart,
      getTotal,
      getItemCount,
      isInCart,
      refreshCart: fetchCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};
